import { GCUsageInfo } from "@app/shared";
import { HardwareStatusChart, HardwareStatusChartProps, SeriesDetails } from "./HardwareStatusChart";
import { ChartProps } from "react-chartjs-2";
import { deepMerge } from "../../../helpers";
import { TooltipItem } from "chart.js";

type GCMetricsChartProps = Omit<HardwareStatusChartProps<GCUsageInfo>, "series">;

const dataAccessors = (index: keyof GCUsageInfo) => {
    return {
        date: (data?: GCUsageInfo) => data?.statusTimestamp,
        xAccessor: (data?: GCUsageInfo) => data?.statusTimestamp,
        yAccessor: (data?: GCUsageInfo) => {
            return data?.[index] as number | undefined;
        },
    };
};

const charts: SeriesDetails<GCUsageInfo>[] = [
    {
        dataKey: "Gen 0",
        accessors: dataAccessors("gen0Collections"),
    },
    {
        dataKey: "Gen 1",
        accessors: dataAccessors("gen1Collections"),
    },
    {
        dataKey: "Gen 2",
        accessors: dataAccessors("gen2Collections"),
    },
    {
        dataKey: "Pinned Objects",
        accessors: dataAccessors("pinnedObjectsCount"),
    }
];

export const GCMetricsChart: React.FC<GCMetricsChartProps> = ({ data = [], ...props }) => {

    const sortedData = data.map(x => ({ ...x })).sort((a, b) =>
        new Date(a.statusTimestamp!).getTime() - new Date(b.statusTimestamp!).getTime()
    );
    const originalSortedData = sortedData.map(x => ({ ...x })); // snapshot totals before mutation
    let largest = 0;
    const restartTimestamps: Date[] = [];
    for (let i = sortedData.length - 1; i > 0; i--) {// clamp to 0; process restarts can cause - numbers as counters will have reset
        const g0 = sortedData[i].gen0Collections - sortedData[i - 1].gen0Collections;
        const g1 = sortedData[i].gen1Collections - sortedData[i - 1].gen1Collections;
        const g2 = sortedData[i].gen2Collections - sortedData[i - 1].gen2Collections;
        const pins = sortedData[i].pinnedObjectsCount - sortedData[i - 1].pinnedObjectsCount;

        // Detect restart: any counter went negative
        if (g0 < 0 || g1 < 0 || g2 < 0) {
            restartTimestamps.push(sortedData[i].statusTimestamp!);
        }

        sortedData[i].gen0Collections = Math.max(0, g0);
        sortedData[i].gen1Collections = Math.max(0, g1);
        sortedData[i].gen2Collections = Math.max(0, g2);
        sortedData[i].pinnedObjectsCount = Math.max(0, pins);

        largest = Math.max(largest, sortedData[i].gen0Collections, sortedData[i].gen1Collections, sortedData[i].gen2Collections);
    }
    if (sortedData.length > 1) {
        sortedData[0] = { ...sortedData[1] };//since we don't have an earlier data point to find the delta just make it the same so we don't have huge initial jump
    }
    const domain = [0, largest] as [number, number];

    const restartAnnotations = Object.fromEntries(
        restartTimestamps.map((ts, i) => [
            `restart_${i}`,
            {
                type: 'line' as const,
                scaleID: 'x',
                value: ts,
                borderColor: 'rgb(201, 122, 93)',
                borderWidth: 2,
                borderDash: [6, 3],
                label: {
                    display: true,
                    content: 'Process Restart',
                    position: 'start' as const,
                    backgroundColor: 'rgb(201, 122, 93)',
                    color: '#fff',
                    font: { size: 10 },
                    padding: 4,
                },
            }
        ])
    );
    var chartProps = {
        options: {
            plugins: {
                tooltip: {
                    callbacks: {
                        afterBody: function (tooltipItems: TooltipItem<"line">[]) {
                            const idx = tooltipItems[0].dataIndex;
                            if (idx < 1) return [];
                            const current = sortedData[idx];
                            const prev = sortedData[idx - 1];
                            const recentPause = current.totalPauseDurationMilSec - prev.totalPauseDurationMilSec;
                            const totals = originalSortedData[idx];
                            return [
                                `Total Collections: Gen0: ${totals.gen0Collections}, Gen1: ${totals.gen1Collections}, Gen2: ${totals.gen2Collections}, Pins: ${totals.pinnedObjectsCount}`,
                                `Recent GC Pause: ${recentPause < 0 ? '-' : (Math.round(recentPause) + ' ms')}, Total GC Pause: ${Math.round(current.totalPauseDurationMilSec)} ms`
                            ];
                        }
                    },
                },
                annotation: {
                    annotations: restartAnnotations,
                },
            },
        },
    } as any as Partial<ChartProps<"line">>;
    const mergeProps = deepMerge(chartProps, props.chartjsOverrides as any) as any;
    return <HardwareStatusChart {...props} domain={domain} data={sortedData} series={charts} yUnit="" chartjsOverrides={mergeProps} />;

};
